import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CreditCardIcon, CheckIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const MVB_PRICE = 2500;
export function PricingCard() {
  const [openItem, setOpenItem] = useState<string | null>('package-1');
  const [showBankDetails, setShowBankDetails] = useState(false);
  const toggleItem = (id: string) => {
    setOpenItem(openItem === id ? null : id);
  };
  return <section id="pricing" className="py-20 md:py-28 px-4 md:px-6 border-b border-white/[0.06]" data-id="element-97">
      <div className="max-w-5xl mx-auto" data-id="element-98">
        <ScrollReveal data-id="element-99">
          <div className="text-center mb-12 md:mb-16" data-id="element-100">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4" data-id="element-101">
              Pricing
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight" data-id="element-102">
              Investment
            </h2>
          </div>
        </ScrollReveal>

        <div className="space-y-4" data-id="element-103">
          {/* PACKAGE 1: MVB (Featured) */}
          <ScrollReveal direction="up" data-id="element-104">
            <div className={`bg-surface border transition-colors duration-300 overflow-hidden ${openItem === 'package-1' ? 'border-accent/50 shadow-[0_0_30px_-10px_rgba(255,107,53,0.15)]' : 'border-white/[0.06] hover:border-white/[0.1]'}`} data-id="element-105">
              <button onClick={() => toggleItem('package-1')} className="w-full flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 text-left gap-4 relative" data-id="element-106">
                {openItem === 'package-1' && <motion.div layoutId="active-border" className="absolute top-0 left-0 w-1 h-full bg-accent" data-id="element-107" />}

                <div className="flex-1 pl-2 md:pl-0" data-id="element-108">
                  <div className="flex items-center gap-3 mb-2" data-id="element-109">
                    <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight" data-id="element-110">
                      Minimum Viable Brand
                    </h3>
                    <span className="bg-accent text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest rounded-sm" data-id="element-111">
                      Recommended
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm md:text-base font-light" data-id="element-112">
                    A focused brand foundation for businesses that need to look
                    credible fast.
                  </p>
                </div>

                <div className="flex items-center justify-between md:justify-end gap-6 pl-2 md:pl-0 w-full md:w-auto" data-id="element-113">
                  <div className="text-left md:text-right" data-id="element-114">
                    <div className="text-xl md:text-2xl font-black text-white tracking-tight" data-id="element-115">
                      £{MVB_PRICE.toLocaleString()}{' '}
                      <span className="text-sm font-normal text-zinc-500" data-id="element-116">
                        + VAT
                      </span>
                    </div>
                    <div className="text-zinc-500 font-mono text-xs uppercase tracking-wider" data-id="element-117">
                      4 weeks duration
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openItem === 'package-1' ? 'rotate-180 text-accent' : ''}`} data-id="element-118" />
                </div>
              </button>

              <AnimatePresence data-id="element-119">
                {openItem === 'package-1' && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-120">
                    <div className="px-6 md:px-8 pb-8 md:pb-12 border-t border-white/[0.06] pt-8" data-id="element-121">
                      <div className="mb-10 max-w-3xl" data-id="element-122">
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed font-light" data-id="element-123">
                          Built for clarity and usability, not excess. By the
                          end of 4 weeks you will have a confident brand
                          foundation ready to use.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 mb-12" data-id="element-124">
                        <div data-id="element-125">
                          <h4 className="text-xs font-bold text-accent tracking-[0.2em] uppercase mb-6" data-id="element-126">
                            What you will leave with
                          </h4>
                          <ul className="space-y-4" data-id="element-127">
                            {['Considered logo exploration refined into one final mark', 'Primary and secondary logo variations', 'Colour palette and typography direction', 'Core brand assets prepared for use', 'Simple brand guidelines for consistency', 'Organised handover'].map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-300" data-id="element-128">
                                <CheckIcon className="w-4 h-4 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-129" />
                                {item}
                              </li>)}
                          </ul>
                        </div>

                        <div data-id="element-130">
                          <h4 className="text-xs font-bold text-accent tracking-[0.2em] uppercase mb-6" data-id="element-131">
                            Design Applications
                          </h4>
                          <p className="text-xs text-zinc-500 mb-6 uppercase tracking-wide" data-id="element-132">
                            Up to five focused applications included:
                          </p>

                          <div className="space-y-6" data-id="element-133">
                            <div data-id="element-134">
                              <h5 className="text-white text-sm font-bold uppercase tracking-wide mb-2" data-id="element-135">
                                1. Social Media Template Set
                              </h5>
                              <p className="text-xs text-zinc-400 leading-relaxed" data-id="element-136">
                                Post template, Quote template, Itinerary
                                highlight template, Testimonial template, Story
                                template
                              </p>
                            </div>
                            <div data-id="element-137">
                              <h5 className="text-white text-sm font-bold uppercase tracking-wide mb-2" data-id="element-138">
                                2. Website Hero Direction
                              </h5>
                              <p className="text-xs text-zinc-400 leading-relaxed" data-id="element-139">
                                Hero layout, headline hierarchy, image guidance,
                                call to action placement
                              </p>
                            </div>
                            <div data-id="element-140">
                              <h5 className="text-white text-sm font-bold uppercase tracking-wide mb-2" data-id="element-141">
                                3. Email Newsletter Template
                              </h5>
                              <p className="text-xs text-zinc-400 leading-relaxed" data-id="element-142">
                                Header layout, destination feature block,
                                testimonial section, call to action
                              </p>
                            </div>
                            <div data-id="element-143">
                              <h5 className="text-white text-sm font-bold uppercase tracking-wide mb-2" data-id="element-144">
                                4. Email Signature Template
                              </h5>
                              <p className="text-xs text-zinc-400 leading-relaxed" data-id="element-145">
                                Clean, professional layout aligned with the new
                                brand
                              </p>
                            </div>
                            <div data-id="element-146">
                              <h5 className="text-white text-sm font-bold uppercase tracking-wide mb-2" data-id="element-147">
                                5. LinkedIn Assets
                              </h5>
                              <p className="text-xs text-zinc-400 leading-relaxed" data-id="element-148">
                                Company banner design, LinkedIn company page
                                visual setup guidance
                              </p>
                            </div>
                          </div>

                          <p className="mt-6 text-[10px] text-zinc-600 uppercase tracking-widest border-t border-white/[0.06] pt-4" data-id="element-149">
                            *Practical working examples, not full rollouts
                          </p>
                        </div>
                      </div>

                      <div className="bg-surface-elevated border border-white/[0.06] p-6 md:p-8 mb-8" data-id="element-150">
                        <h4 className="text-xs font-bold text-zinc-500 tracking-[0.2em] uppercase mb-6" data-id="element-151">
                          Payment Schedule
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-152">
                          <div className="flex items-start" data-id="element-153">
                            <div className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mr-4 flex-shrink-0 text-accent font-mono text-xs font-bold" data-id="element-154">
                              1
                            </div>
                            <div data-id="element-155">
                              <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-156">
                                50% Deposit
                              </div>
                              <div className="text-zinc-400 text-xs font-mono" data-id="element-157">
                                £1,250 + VAT • Due upon signing
                              </div>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-158">
                            <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mr-4 flex-shrink-0 text-zinc-400 font-mono text-xs font-bold" data-id="element-159">
                              2
                            </div>
                            <div data-id="element-160">
                              <div className="text-white font-bold text-sm uppercase tracking-wide mb-1" data-id="element-161">
                                50% Completion
                              </div>
                              <div className="text-zinc-400 text-xs font-mono" data-id="element-162">
                                £1,250 + VAT • Due on handover
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-163">
                        <button onClick={e => {
                      e.stopPropagation();
                      setShowBankDetails(!showBankDetails);
                    }} className="flex items-center text-xs md:text-sm font-bold text-zinc-400 hover:text-white transition-colors uppercase tracking-widest" data-id="element-164">
                          <CreditCardIcon className="w-4 h-4 mr-3" data-id="element-165" />
                          {showBankDetails ? 'Hide bank details' : 'Show bank details'}
                          <ChevronDownIcon className={`w-4 h-4 ml-2 transition-transform ${showBankDetails ? 'rotate-180' : ''}`} data-id="element-166" />
                        </button>

                        <AnimatePresence data-id="element-167">
                          {showBankDetails && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden" data-id="element-168">
                              <div className="mt-6 p-6 bg-[#050507] text-white border border-white/[0.06]" data-id="element-169">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm" data-id="element-170">
                                  <div data-id="element-171">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-172">
                                      Bank
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-173">
                                      Clearbank
                                    </span>
                                  </div>
                                  <div data-id="element-174">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-175">
                                      Account name
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-176">
                                      AVY Consultancy Limited
                                    </span>
                                  </div>
                                  <div data-id="element-177">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-178">
                                      Company number
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-179">
                                      12143361
                                    </span>
                                  </div>
                                  <div data-id="element-180">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-181">
                                      Sort code
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-182">
                                      04-06-05
                                    </span>
                                  </div>
                                  <div data-id="element-183">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-184">
                                      Account no
                                    </span>
                                    <span className="font-mono text-zinc-300" data-id="element-185">
                                      16926743
                                    </span>
                                  </div>
                                  <div data-id="element-186">
                                    <span className="block text-zinc-600 text-[10px] uppercase tracking-widest mb-1" data-id="element-187">
                                      Reference
                                    </span>
                                    <span className="font-mono text-accent" data-id="element-188">
                                      SOLOQUEST-MVB-2026
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </ScrollReveal>

          {/* OTHER PACKAGES */}
          {[{
          id: 'package-2',
          title: 'Website Sprint',
          price: '£1,300',
          duration: '2–3 weeks',
          description: 'A focused website design and build to get your brand online with confidence. Clean, professional and ready to convert visitors into enquiries.',
          deliverables: ['Homepage design tailored to your brand', 'Up to 5 inner pages (About, Services, Contact, etc.)', 'Mobile responsive across all devices', 'Basic SEO setup and meta data', 'Contact form and enquiry integration', 'CMS setup for easy self-management', 'Launch support and handover']
        }, {
          id: 'package-3',
          title: 'Website Support',
          price: '£150/mo',
          duration: '12 months agreement',
          description: 'Ongoing website care so you can focus on running your business. We keep things updated, secure and performing well.',
          deliverables: ['Monthly platform and plugin updates', 'Security monitoring and backups', 'Content updates (up to 4 per month)', 'Performance and uptime monitoring', 'Priority email support', 'Monthly summary report']
        }, {
          id: 'package-4',
          title: 'Website and Creative',
          price: '£550/mo',
          duration: 'Ongoing monthly',
          description: 'Flexible monthly design and brand support for when you need a creative partner on call. Ideal for keeping your brand consistent as you grow.',
          deliverables: ['Up to 10 hours of design time per month', 'Social media graphics and templates', 'Marketing collateral (flyers, PDFs, ads)', 'Brand consistency reviews', 'Priority turnaround on requests', 'Unused hours do not roll over']
        }].map((pkg, index) => <ScrollReveal key={pkg.id} direction="up" delay={0.1 + index * 0.05} data-id="element-189">
              <div className={`bg-surface border transition-colors duration-300 overflow-hidden ${openItem === pkg.id ? 'border-white/[0.12]' : 'border-white/[0.06] hover:border-white/[0.1]'}`} data-id="element-190">
                <button onClick={() => toggleItem(pkg.id)} className="w-full flex flex-col md:flex-row md:items-center justify-between p-6 md:p-8 text-left gap-4" data-id="element-191">
                  <h3 className={`text-lg md:text-xl font-bold uppercase tracking-tight transition-colors ${openItem === pkg.id ? 'text-white' : 'text-zinc-400'}`} data-id="element-192">
                    {pkg.title}
                  </h3>
                  <div className="flex items-center gap-6" data-id="element-193">
                    <div className="text-left md:text-right" data-id="element-194">
                      <span className="text-lg md:text-xl font-black text-white tracking-tight" data-id="element-195">
                        {pkg.price}
                      </span>
                      <span className="text-sm font-normal text-zinc-500 ml-1" data-id="element-196">
                        + VAT
                      </span>
                      <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider" data-id="element-197">
                        {pkg.duration}
                      </div>
                    </div>
                    <ChevronDownIcon className={`w-5 h-5 text-zinc-500 transition-transform duration-300 flex-shrink-0 ${openItem === pkg.id ? 'rotate-180 text-white' : ''}`} data-id="element-198" />
                  </div>
                </button>
                <AnimatePresence data-id="element-199">
                  {openItem === pkg.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-200">
                      <div className="px-6 md:px-8 pb-8 border-t border-white/[0.06] pt-6" data-id="element-201">
                        <p className="text-zinc-300 text-sm md:text-base font-light leading-relaxed mb-6 max-w-2xl" data-id="element-202">
                          {pkg.description}
                        </p>
                        <h4 className="text-[10px] font-bold text-zinc-500 tracking-[0.2em] uppercase mb-4" data-id="element-203">
                          What's included
                        </h4>
                        <ul className="space-y-3" data-id="element-204">
                          {pkg.deliverables.map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-205">
                              <CheckIcon className="w-3.5 h-3.5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-206" />
                              {item}
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </ScrollReveal>)}
        </div>
      </div>
    </section>;
}